import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { FiPlus, FiCheck, FiX, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmModal';
import './HR.css';

const ALL_LEAVE_TYPES = ['annual','sick','casual','unpaid'];
const STATUS_COLOR = { pending:'#ed8936', approved:'#48bb78', rejected:'#f56565', cancelled:'#a0aec0' };
const LEAVE_LABELS = { annual:'Annual Leave', sick:'Sick Leave', casual:'Casual Leave', unpaid:'Unpaid Leave' };
const LEAVE_ICONS  = { annual:'🌴', sick:'🤒', casual:'☀️', unpaid:'⏸️' };

const POLICY_YEAR = new Date().getFullYear();

const Leaves = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const confirm = useConfirm();

  // Employee state
  const [leaves,   setLeaves]   = useState([]);
  const [balance,  setBalance]  = useState(null);

  // Admin state
  const [allLeaves,   setAllLeaves]   = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterYear,     setFilterYear]     = useState(String(new Date().getFullYear()));
  const [stats, setStats] = useState({ total:0, pending:0, approved:0, rejected:0 });
  const [adminTab, setAdminTab] = useState('leaves'); // 'leaves' | 'policy' | 'balances'

  // Leave policy state
  const [policy, setPolicy]   = useState(null);
  const [policyYear, setPolicyYear] = useState(POLICY_YEAR);
  const [policyForm, setPolicyForm] = useState({ annualTotal:18, sickTotal:12, casualTotal:6, unpaidTotal:0 });
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [applyingPolicy, setApplyingPolicy] = useState(false);

  // Team balances state
  const [teamBalances, setTeamBalances] = useState([]);
  const [editingBalance, setEditingBalance] = useState(null); // { empId, type, current }
  const [editValue, setEditValue] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ leaveType:'annual', startDate:'', endDate:'', reason:'', isHalfDay:false, halfDayPeriod:'morning' });

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadMyLeaves = useCallback(async () => {
    try {
      const r = await api.get('/leaves/my');
      setLeaves(r.data.data);
      setBalance(r.data.balance);
    } catch(e) { console.error(e); }
  }, []);

  const loadAllLeaves = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus)   params.status   = filterStatus;
      if (filterEmployee) params.employee = filterEmployee;
      if (filterYear)     params.year     = filterYear;
      const r = await api.get('/leaves', { params });
      const data = r.data.data || [];
      setAllLeaves(data);
      setStats({
        total:    data.length,
        pending:  data.filter(l => l.status === 'pending').length,
        approved: data.filter(l => l.status === 'approved').length,
        rejected: data.filter(l => l.status === 'rejected').length,
      });
    } catch(e) { console.error(e); }
  }, [filterStatus, filterEmployee, filterYear]);

  const loadEmployees = useCallback(async () => {
    try {
      const r = await api.get('/users?role=team');
      setEmployees(r.data.users || []);
    } catch(e) { console.error(e); }
  }, []);

  const loadPolicy = useCallback(async (year) => {
    try {
      const r = await api.get(`/leaves/policy?year=${year}`);
      const p = r.data.data;
      setPolicy(p);
      setPolicyForm({ annualTotal: p.annualTotal, sickTotal: p.sickTotal, casualTotal: p.casualTotal, unpaidTotal: p.unpaidTotal });
    } catch(e) { console.error(e); }
  }, []);

  const loadTeamBalances = useCallback(async () => {
    try {
      const r = await api.get(`/leaves/team-summary?year=${filterYear}`);
      setTeamBalances(r.data.data || []);
    } catch(e) { console.error(e); }
  }, [filterYear]);

  useEffect(() => {
    if (isAdmin) { loadAllLeaves(); loadEmployees(); loadPolicy(policyYear); loadTeamBalances(); }
    loadMyLeaves();
  }, [isAdmin, loadAllLeaves, loadEmployees, loadMyLeaves, loadPolicy, loadTeamBalances, policyYear]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const applyLeave = async (e) => {
    e.preventDefault();
    if (!form.startDate || !form.endDate) return toast.error('Please select start and end dates');
    if (form.endDate < form.startDate)    return toast.error('End date cannot be before start date');
    const today = new Date().toISOString().slice(0,10);
    if (form.startDate < today) return toast.error('Cannot apply leave for past dates');
    try {
      await api.post('/leaves', form);
      toast.success('Leave applied successfully!');
      setShowModal(false);
      loadMyLeaves();
      if (isAdmin) loadAllLeaves();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const processLeave = async (id, status) => {
    try {
      await api.patch(`/leaves/${id}/process`, { status });
      toast.success(`Leave ${status}`);
      loadAllLeaves();
      loadMyLeaves();
      loadTeamBalances();
    } catch(e) { toast.error('Failed'); }
  };

  const cancelLeave = async (id) => {
    const ok = await confirm('Cancel this leave request?', { title: 'Cancel Leave?', variant: 'warning' });
    if (!ok) return;
    try {
      await api.patch(`/leaves/${id}/cancel`);
      toast.success('Leave cancelled');
      loadMyLeaves();
      if (isAdmin) loadAllLeaves();
    } catch(e) { toast.error('Failed'); }
  };

  const deleteLeave = async (id) => {
    const ok = await confirm('Permanently delete this leave record?', { title: 'Delete Leave?', variant: 'danger' });
    if (!ok) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success('Leave deleted');
      loadAllLeaves();
      loadMyLeaves();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await api.put('/leaves/policy', { year: policyYear, ...policyForm });
      toast.success('Leave policy saved!');
      loadPolicy(policyYear);
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    setSavingPolicy(false);
  };

  const applyPolicyToAll = async () => {
    const ok = await confirm(`Apply this leave policy to ALL team members for ${policyYear}?\n\nThis will update their leave totals but will NOT reset used days.`, { title: 'Apply to All Employees?', variant: 'warning' });
    if (!ok) return;
    setApplyingPolicy(true);
    try {
      const r = await api.post('/leaves/policy/apply-all', { year: policyYear });
      toast.success(r.data.message);
      loadTeamBalances();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    setApplyingPolicy(false);
  };

  const saveIndividualBalance = async () => {
    if (!editingBalance) return;
    try {
      await api.post('/leaves/balance/update', { employeeId: editingBalance.empId, year: filterYear, leaveType: editingBalance.type, total: parseFloat(editValue) });
      toast.success('Balance updated');
      setEditingBalance(null);
      loadTeamBalances();
    } catch(e) { toast.error('Failed'); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '-';

  const INP = { padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', width:'100%', boxSizing:'border-box', fontFamily:'inherit' };
  const BTN = (v) => ({ padding:'10px 22px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:14, display:'inline-flex', alignItems:'center', gap:6,
    ...(v==='primary'?{background:'var(--pm-gradient)',color:'#fff'}:
        v==='warning'?{background:'#fef3c7',color:'#92400e',border:'1.5px solid #fcd34d'}:
        {background:'#f8fafc',color:'#64748b',border:'1.5px solid #e2e8f0'}) });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="hr-page">
      <div className="page-header">
        <h1>Leave Management</h1>
        {!isAdmin && (
          <button onClick={() => setShowModal(true)}
            style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'10px 20px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:14, background:'var(--pm-gradient)', color:'#fff' }}>
            <FiPlus /> Apply Leave
          </button>
        )}
      </div>

      {/* ── ADMIN VIEW ──────────────────────────────────────────────────────── */}
      {isAdmin ? (
        <>
          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 }}>
            {[
              { label:'Total',    value: stats.total,    color:'var(--pm-primary)' },
              { label:'Pending',  value: stats.pending,  color:'#ed8936' },
              { label:'Approved', value: stats.approved, color:'#48bb78' },
              { label:'Rejected', value: stats.rejected, color:'#f56565' },
            ].map(s => (
              <div key={s.label} className="card" style={{ textAlign:'center', padding:'20px 16px' }}>
                <div style={{ fontSize:32, fontWeight:700, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:13, color:'#94a3b8', marginTop:4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Admin Tabs */}
          <div style={{ display:'flex', gap:4, marginBottom:20, background:'#f1f5f9', padding:4, borderRadius:12, width:'fit-content' }}>
            {[['leaves','📋 Leave Requests'],['policy','⚙️ Leave Policy'],['balances','👥 Employee Balances']].map(([tab, label]) => (
              <button key={tab} onClick={() => setAdminTab(tab)} style={{
                padding:'8px 18px', borderRadius:9, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
                background: adminTab===tab ? '#fff' : 'transparent',
                color: adminTab===tab ? 'var(--pm-primary)' : '#64748b',
                boxShadow: adminTab===tab ? '0 1px 4px #0000001a' : 'none',
                transition:'all .15s'
              }}>{label}</button>
            ))}
          </div>

          {/* ── Tab: Leave Requests ── */}
          {adminTab === 'leaves' && (
            <>
              {/* Filters */}
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
                  <select className="form-control" style={{ width:160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <select className="form-control" style={{ width:180 }} value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
                    <option value="">All Employees</option>
                    {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                  </select>
                  <select className="form-control" style={{ width:120 }} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                    {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div className="card">
                <h2 style={{ marginBottom:16 }}>All Employee Leaves ({allLeaves.length})</h2>
                <div className="table-wrap">
                  <table className="hr-table">
                    <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                      {allLeaves.length === 0 && <tr><td colSpan="8" className="empty">No leave records found</td></tr>}
                      {allLeaves.map(l => (
                        <tr key={l._id}>
                          <td>
                            <div style={{ fontWeight:600 }}>{l.employee?.name}</div>
                            <div style={{ fontSize:11, color:'#94a3b8' }}>{l.employee?.position || l.employee?.email}</div>
                          </td>
                          <td style={{ textTransform:'capitalize' }}>{l.leaveType}</td>
                          <td>{fmt(l.startDate)}</td>
                          <td>{fmt(l.endDate)}</td>
                          <td style={{ fontWeight:600, color:'var(--pm-primary)' }}>{l.totalDays}</td>
                          <td style={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.reason}</td>
                          <td><span className="status-badge" style={{ background: STATUS_COLOR[l.status] || '#a0aec0' }}>{l.status}</span></td>
                          <td>
                            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                              <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                                {l.status !== 'cancelled' && l.status !== 'approved' && (
                                  <button title="Approve" onClick={() => processLeave(l._id, 'approved')}
                                    style={{ height:28, padding:'0 10px', borderRadius:8, border:'none', cursor:'pointer', background:'#dcfce7', color:'#16a34a', display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600 }}>
                                    <FiCheck size={12}/> Approve
                                  </button>
                                )}
                                {l.status !== 'cancelled' && l.status !== 'rejected' && (
                                  <button title="Reject" onClick={() => processLeave(l._id, 'rejected')}
                                    style={{ height:28, padding:'0 10px', borderRadius:8, border:'none', cursor:'pointer', background:'#fee2e2', color:'#ef4444', display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600 }}>
                                    <FiX size={12}/> Reject
                                  </button>
                                )}
                                <button title="Delete" onClick={() => deleteLeave(l._id)}
                                  style={{ height:28, padding:'0 10px', borderRadius:8, border:'none', cursor:'pointer', background:'#f1f5f9', color:'#94a3b8', display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:600 }}>
                                  <FiTrash2 size={12}/> Delete
                                </button>
                              </div>
                              {l.approvedBy && <span style={{ fontSize:10, color:'#94a3b8' }}>By {l.approvedBy.name}</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ── Tab: Leave Policy ── */}
          {adminTab === 'policy' && (
            <div className="card" style={{ maxWidth: 700 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
                <div>
                  <h2 style={{ margin:0, fontSize:20, fontWeight:700 }}>Company Leave Policy</h2>
                  <p style={{ margin:'6px 0 0', color:'#64748b', fontSize:13 }}>Set the number of leave days each employee gets per year. Use "Apply to All" to push changes to everyone.</p>
                </div>
                <select value={policyYear} onChange={e => { setPolicyYear(parseInt(e.target.value)); loadPolicy(parseInt(e.target.value)); }}
                  style={{ ...INP, width:120 }}>
                  {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:28 }}>
                {ALL_LEAVE_TYPES.map(type => (
                  <div key={type} style={{ background:'#f8fafc', borderRadius:12, padding:16, border:'1.5px solid #e2e8f0' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                      <span style={{ fontSize:20 }}>{LEAVE_ICONS[type]}</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14, color:'#1e293b' }}>{LEAVE_LABELS[type]}</div>
                        <div style={{ fontSize:11, color:'#94a3b8' }}>days per year</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <input type="number" min="0" max="365" step="0.5"
                        value={policyForm[`${type}Total`]}
                        onChange={e => setPolicyForm(f => ({ ...f, [`${type}Total`]: e.target.value }))}
                        style={{ ...INP, width:90, fontSize:20, fontWeight:700, textAlign:'center', color:'var(--pm-primary)' }}
                      />
                      <span style={{ fontSize:13, color:'#64748b' }}>days</span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                <button onClick={savePolicy} disabled={savingPolicy} style={BTN('primary')}>
                  {savingPolicy ? '⏳ Saving…' : '💾 Save Policy'}
                </button>
                <button onClick={applyPolicyToAll} disabled={applyingPolicy} style={BTN('warning')}>
                  {applyingPolicy ? '⏳ Applying…' : `🔄 Apply to All Employees (${policyYear})`}
                </button>
              </div>

              <div style={{ marginTop:20, padding:14, background:'#fffbeb', borderRadius:10, border:'1px solid #fcd34d', fontSize:13, color:'#92400e' }}>
                <strong>ℹ️ How it works:</strong> Saving the policy stores the defaults for new employees.
                "Apply to All" updates <em>existing</em> employees' totals for {policyYear} without resetting used days.
              </div>
            </div>
          )}

          {/* ── Tab: Employee Balances ── */}
          {adminTab === 'balances' && (
            <div className="card">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>
                <div>
                  <h2 style={{ margin:0, fontSize:20, fontWeight:700 }}>Employee Leave Balances</h2>
                  <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:13 }}>Click any cell to edit that employee's leave allocation for the year.</p>
                </div>
                <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
                  style={{ ...INP, width:120 }}>
                  {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table className="hr-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      {ALL_LEAVE_TYPES.map(t => <th key={t} style={{ textAlign:'center' }}>{LEAVE_ICONS[t]} {t.charAt(0).toUpperCase()+t.slice(1)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {teamBalances.length === 0 && <tr><td colSpan={ALL_LEAVE_TYPES.length+1} className="empty">No balances found. Use "Apply to All" in Leave Policy tab first.</td></tr>}
                    {teamBalances.map(b => (
                      <tr key={b.id}>
                        <td>
                          <div style={{ fontWeight:600 }}>{b.employee?.name}</div>
                          <div style={{ fontSize:11, color:'#94a3b8' }}>{b.employee?.position || b.employee?.email}</div>
                        </td>
                        {ALL_LEAVE_TYPES.map(type => {
                          const usedKey   = `${type}Used`;
                          const totalKey  = `${type}Total`;
                          const used  = parseFloat(b[usedKey]  || 0);
                          const total = parseFloat(b[totalKey] || 0);
                          const left  = total - used;
                          const isEditing = editingBalance?.empId === b.employeeId && editingBalance?.type === type;
                          return (
                            <td key={type} style={{ textAlign:'center', cursor:'pointer' }}
                              onClick={() => { if(!isEditing) { setEditingBalance({ empId: b.employeeId, type }); setEditValue(String(total)); } }}>
                              {isEditing ? (
                                <div style={{ display:'flex', alignItems:'center', gap:4, justifyContent:'center' }}>
                                  <input type="number" min="0" step="0.5" value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    style={{ width:60, padding:'4px 6px', border:'2px solid #6366f1', borderRadius:6, textAlign:'center', fontSize:13, outline:'none' }}
                                    autoFocus
                                  />
                                  <button onClick={e => { e.stopPropagation(); saveIndividualBalance(); }}
                                    style={{ background:'#22c55e', color:'#fff', border:'none', borderRadius:6, width:24, height:24, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12 }}>✓</button>
                                  <button onClick={e => { e.stopPropagation(); setEditingBalance(null); }}
                                    style={{ background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:6, width:24, height:24, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12 }}>✕</button>
                                </div>
                              ) : (
                                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                                  <span style={{ fontSize:13, fontWeight:700, color: left < 2 ? '#ef4444' : '#22c55e' }}>{left}</span>
                                  <span style={{ fontSize:10, color:'#94a3b8' }}>{used}/{total}</span>
                                  <div style={{ width:40, height:3, background:'#e2e8f0', borderRadius:2, overflow:'hidden' }}>
                                    <div style={{ width:`${total>0?(used/total)*100:0}%`, height:'100%', background: used/total > 0.8 ? '#ef4444' : 'var(--pm-primary)', borderRadius:2 }} />
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ── EMPLOYEE VIEW ──────────────────────────────────────────────────── */
        <>
          {/* Leave Balance */}
          {balance && (
            <div className="card">
              <h2>Leave Balance - {new Date().getFullYear()}</h2>
              <div className="balance-grid">
                {ALL_LEAVE_TYPES.filter(type => parseFloat(balance[type]?.total || 0) > 0).map(type => {
                  const b = balance[type] || {};
                  const isAnnual = type === 'annual';
                  // Annual: use accrual-based available; others: total - used
                  const avail = isAnnual
                    ? (b.available ?? Math.max(0, (b.accrued||0) + (b.carryForward||0) - (b.used||0)))
                    : Math.max(0, (b.total||0) - (b.used||0));
                  const barMax = isAnnual ? ((b.accrued||0) + (b.carryForward||0)) || 1 : (b.total||1);
                  const barUsed = Math.min(100, ((b.used||0) / barMax) * 100);
                  return (
                    <div key={type} className="balance-card">
                      <div className="balance-type">{LEAVE_ICONS[type]} {LEAVE_LABELS[type]}</div>
                      <div className="balance-count">
                        <span className="used">{b.used||0}</span>
                        <span className="sep">/</span>
                        <span className="total">{isAnnual ? (b.accrued||0) + (b.carryForward||0) : b.total||0}</span>
                      </div>
                      <div className="balance-bar">
                        <div className="balance-fill" style={{width:`${barUsed}%`}}></div>
                      </div>
                      <div className="balance-avail">{avail} days left</div>
                      {isAnnual && (
                        <div style={{ fontSize:11, color:'#94a3b8', marginTop:4, lineHeight:1.4 }}>
                          {b.accrued||0} accrued this year
                          {(b.carryForward||0) > 0 && <span style={{ color:'#6366f1', marginLeft:4 }}>+{b.carryForward} carried forward</span>}
                          <span style={{ color:'#cbd5e1', marginLeft:4 }}>of {b.total||0} annual</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* My Leave History */}
          <div className="card">
            <h2>My Leave History</h2>
            <div className="table-wrap">
              <table className="hr-table">
                <thead><tr><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {leaves.map(l => (
                    <tr key={l._id}>
                      <td style={{ textTransform:'capitalize' }}>{l.leaveType}</td>
                      <td>{fmt(l.startDate)}</td>
                      <td>{fmt(l.endDate)}</td>
                      <td>{l.totalDays}</td>
                      <td>{l.reason}</td>
                      <td><span className="status-badge" style={{background:STATUS_COLOR[l.status]}}>{l.status}</span></td>
                      <td>{l.status==='pending' && <button onClick={()=>cancelLeave(l._id)}
                        style={{ padding:'4px 12px', borderRadius:8, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', cursor:'pointer', fontWeight:600, fontSize:12 }}>Cancel</button>}</td>
                    </tr>
                  ))}
                  {leaves.length===0 && <tr><td colSpan="7" className="empty">No leaves applied</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Apply Leave Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="modal-content" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>Apply for Leave</h2>
              <button className="close-btn" onClick={()=>setShowModal(false)}>×</button>
            </div>
            <form onSubmit={applyLeave}>
              <div className="form-group">
                <label>Leave Type</label>
                <select className="form-control" value={form.leaveType} onChange={e=>setForm({...form,leaveType:e.target.value})}>
                  {ALL_LEAVE_TYPES.filter(t => parseFloat(balance?.[t]?.total || 0) > 0 || t === 'unpaid').map(t=><option key={t} value={t}>{LEAVE_LABELS[t]}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>From Date</label>
                  <input type="date" className="form-control" value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value, endDate: f.endDate < e.target.value ? e.target.value : (f.endDate || e.target.value) }))} required />
                </div>
                <div className="form-group">
                  <label>To Date <span style={{fontSize:11,color:'#94a3b8',fontWeight:400}}>(same as From = 1 day)</span></label>
                  <input type="date" className="form-control" value={form.endDate}
                    min={form.startDate}
                    onChange={e=>setForm({...form,endDate:e.target.value})} required />
                </div>
              </div>
              <div className="form-group">
                <label>Reason</label>
                <textarea className="form-control" value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} required />
              </div>
              <div className="form-check">
                <input type="checkbox" id="halfDay" checked={form.isHalfDay} onChange={e=>setForm({...form,isHalfDay:e.target.checked})} />
                <label htmlFor="halfDay">Half Day</label>
              </div>
              {form.isHalfDay && (
                <div className="form-group">
                  <select className="form-control" value={form.halfDayPeriod} onChange={e=>setForm({...form,halfDayPeriod:e.target.value})}>
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                  </select>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" onClick={()=>setShowModal(false)}
                  style={{ padding:'10px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', color:'#64748b', cursor:'pointer', fontWeight:600, fontSize:14 }}>Cancel</button>
                <button type="submit"
                  style={{ padding:'10px 20px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:14 }}>Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;
